/**
 * Delivery Route Optimization Agent
 * Optimizes pickup and delivery routes for CSA members and farm deliveries
 */

interface DeliveryStop {
  id: string;
  address: string;
  city: string;
  coordinates: { lat: number; lng: number };
  customerName: string;
  items: string[];
  deliveryWindow?: { start: string; end: string };
  priority: 'high' | 'medium' | 'low';
  notes?: string;
}

interface OptimizedRoute {
  stops: DeliveryStop[];
  totalDistance: number;
  estimatedDuration: number;
  sequence: number[];
  waypoints: { lat: number; lng: number }[];
}

interface RouteAnalytics {
  totalStops: number;
  totalDistance: number;
  fuelCost: number;
  co2Savings: number;
  efficiencyScore: number;
}

class DeliveryRouteAgent {
  private avgSpeedMph: number = 35;
  private fuelEfficiencyMpg: number = 20;
  private gasPrice: number = 3.5;

  /**
   * Optimize delivery route using nearest neighbor algorithm
   * @param startPoint Starting location (farm)
   * @param stops List of delivery stops
   * @returns Optimized route
   */
  optimizeRoute(
    startPoint: { lat: number; lng: number },
    stops: DeliveryStop[]
  ): OptimizedRoute {
    if (stops.length === 0) {
      return {
        stops: [],
        totalDistance: 0,
        estimatedDuration: 0,
        sequence: [],
        waypoints: [],
      };
    }

    const unvisited = [...stops];
    const route: DeliveryStop[] = [];
    const sequence: number[] = [];
    let currentLocation = startPoint;
    let totalDistance = 0;

    // Nearest neighbor algorithm with priority consideration
    while (unvisited.length > 0) {
      let nearestIndex = 0;
      let nearestDistance = Infinity;

      for (let i = 0; i < unvisited.length; i++) {
        const distance = this.calculateDistance(currentLocation, unvisited[i].coordinates);
        // Apply priority weighting
        const weightedDistance =
          unvisited[i].priority === 'high'
            ? distance * 0.7
            : unvisited[i].priority === 'medium'
            ? distance
            : distance * 1.3;

        if (weightedDistance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = i;
        }
      }

      const nextStop = unvisited.splice(nearestIndex, 1)[0];
      route.push(nextStop);
      sequence.push(stops.indexOf(nextStop));
      totalDistance += nearestDistance;
      currentLocation = nextStop.coordinates;
    }

    // Add return to start
    totalDistance += this.calculateDistance(currentLocation, startPoint);

    const waypoints = route.map(stop => stop.coordinates);
    const estimatedDuration = (totalDistance / this.avgSpeedMph) * 60; // in minutes

    return {
      stops: route,
      totalDistance: Math.round(totalDistance * 10) / 10,
      estimatedDuration: Math.round(estimatedDuration),
      sequence,
      waypoints,
    };
  }

  /**
   * Group stops by geographic clusters
   * @param stops List of delivery stops
   * @param maxClusters Maximum number of delivery zones
   * @returns Clustered stops by zone
   */
  clusterByZone(stops: DeliveryStop[], maxClusters: number = 3): Map<string, DeliveryStop[]> {
    const zones = new Map<string, DeliveryStop[]>();
    const cities = new Set(stops.map(s => s.city));

    // Simple clustering by city
    for (const city of cities) {
      const cityStops = stops.filter(s => s.city === city);
      zones.set(city, cityStops);
    }

    // If we have more cities than max clusters, merge nearby cities
    if (zones.size > maxClusters) {
      const merged = this.mergeClusters(zones, maxClusters);
      return merged;
    }

    return zones;
  }

  /**
   * Calculate route analytics
   * @param route Optimized route
   * @returns Analytics data
   */
  calculateRouteAnalytics(route: OptimizedRoute): RouteAnalytics {
    const fuelUsed = route.totalDistance / this.fuelEfficiencyMpg;
    const fuelCost = fuelUsed * this.gasPrice;

    // Calculate CO2 savings compared to individual trips
    const individualTrips = route.stops.length * 2; // Round trip for each
    const avgTripDistance = route.totalDistance / route.stops.length;
    const individualDistance = individualTrips * avgTripDistance;
    const co2PerMile = 0.89; // pounds
    const co2Savings = (individualDistance - route.totalDistance) * co2PerMile;

    // Calculate efficiency score (0-100)
    const idealDistance = route.stops.length * 5; // Assuming 5 miles between stops ideal
    const efficiencyScore = Math.min(100, (idealDistance / route.totalDistance) * 100);

    return {
      totalStops: route.stops.length,
      totalDistance: route.totalDistance,
      fuelCost: Math.round(fuelCost * 100) / 100,
      co2Savings: Math.round(co2Savings * 10) / 10,
      efficiencyScore: Math.round(efficiencyScore),
    };
  }

  /**
   * Suggest optimal delivery days based on stop locations
   * @param stops All delivery stops
   * @returns Recommended delivery schedule
   */
  suggestDeliverySchedule(stops: DeliveryStop[]): Map<string, DeliveryStop[]> {
    const schedule = new Map<string, DeliveryStop[]>();
    const zones = this.clusterByZone(stops, 3);

    const days = ['Monday', 'Wednesday', 'Friday'];
    let dayIndex = 0;

    for (const [zone, zoneStops] of zones.entries()) {
      const day = days[dayIndex % days.length];
      const existing = schedule.get(day) || [];
      schedule.set(day, [...existing, ...zoneStops]);
      dayIndex++;
    }

    return schedule;
  }

  /**
   * Get time windows for route
   * @param route Optimized route
   * @param startTime Starting time (HH:MM format)
   * @returns Delivery time windows for each stop
   */
  calculateTimeWindows(
    route: OptimizedRoute,
    startTime: string = '09:00'
  ): Array<{ stop: DeliveryStop; eta: string; window: string }> {
    const [hours, minutes] = startTime.split(':').map(Number);
    let currentTime = hours * 60 + minutes; // Convert to minutes
    const windows: Array<{ stop: DeliveryStop; eta: string; window: string }> = [];
    let distanceCovered = 0;

    for (let i = 0; i < route.stops.length; i++) {
      // Calculate travel time to this stop
      const segmentDistance =
        i === 0
          ? this.calculateDistance({ lat: 26.6406, lng: -81.8723 }, route.stops[i].coordinates)
          : this.calculateDistance(
              route.stops[i - 1].coordinates,
              route.stops[i].coordinates
            );

      const travelTime = (segmentDistance / this.avgSpeedMph) * 60;
      currentTime += travelTime;

      // Add delivery time (15 minutes per stop)
      const deliveryTime = 15;

      const etaHours = Math.floor(currentTime / 60);
      const etaMinutes = Math.floor(currentTime % 60);
      const eta = `${String(etaHours).padStart(2, '0')}:${String(etaMinutes).padStart(2, '0')}`;

      const windowEndTime = currentTime + deliveryTime;
      const windowHours = Math.floor(windowEndTime / 60);
      const windowMinutes = Math.floor(windowEndTime % 60);
      const windowEnd = `${String(windowHours).padStart(2, '0')}:${String(windowMinutes).padStart(2, '0')}`;

      windows.push({
        stop: route.stops[i],
        eta,
        window: `${eta} - ${windowEnd}`,
      });

      currentTime += deliveryTime;
    }

    return windows;
  }

  // Helper methods

  private calculateDistance(
    point1: { lat: number; lng: number },
    point2: { lat: number; lng: number }
  ): number {
    const R = 3959; // Earth's radius in miles
    const dLat = ((point2.lat - point1.lat) * Math.PI) / 180;
    const dLon = ((point2.lng - point1.lng) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((point1.lat * Math.PI) / 180) *
        Math.cos((point2.lat * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private mergeClusters(
    zones: Map<string, DeliveryStop[]>,
    maxClusters: number
  ): Map<string, DeliveryStop[]> {
    // Simple merge: combine smallest zones
    const merged = new Map<string, DeliveryStop[]>();
    const sorted = Array.from(zones.entries()).sort((a, b) => b[1].length - a[1].length);

    for (let i = 0; i < Math.min(maxClusters, sorted.length); i++) {
      merged.set(sorted[i][0], sorted[i][1]);
    }

    // Merge remaining into closest zones
    for (let i = maxClusters; i < sorted.length; i++) {
      const [city, stops] = sorted[i];
      const firstZone = Array.from(merged.keys())[0];
      const existing = merged.get(firstZone) || [];
      merged.set(firstZone, [...existing, ...stops]);
    }

    return merged;
  }
}

// Export singleton instance
export const deliveryRouteAgent = new DeliveryRouteAgent();
export default deliveryRouteAgent;
